import { botonBitacora } from "./auditoria.js";
import { anticipoAplicado, anticipoDisponible, anticiposVivosCliente, calcularRetencionesVenta, ajustarInventario, crearAsiento, generarAsientoCobroFactura } from "../core/accounting.js";
import { renglonesIVA } from "../core/iva.js";
import { exportPDFFactura } from "../export/pdf.js";
import { facturarLocal, puedeReenviarFactura, showFacturarDataico , showFacturarOP} from "./dataico.js";

import { saveData } from "../core/cloudSync.js";
import { C, ESTADOS, ESTADO_COLOR, MEDIOS_PAGO } from "../core/config.js";
import { state } from "../core/state.js";
import { btnStyle, escapeHTML, fmtCOP, fmtDate, inputStyle, parseNumber, today } from "../core/utils.js";
import { render } from "../ui/app.js";
import { renderNotaCreditoModal } from "./notasCredito.js";
import { htmlParaMostrar } from "../core/textoRico.js";

export function getTotalNC(facturaId) {
  return (state.data.notas_credito||[])
    .filter(nc => nc._origen && nc._origen.id === facturaId && nc.estado !== "anulada")
    .reduce((s,nc) => s + (Number(nc.total)||0), 0);
}

export function getTotalAbonadoFactura(f) {
  if (Array.isArray(f._pagos)) return f._pagos.reduce((s,p) => s + (parseNumber(p.monto)||0), 0);
  return 0;
}

// Lo retenido por el cliente (agente retenedor) NO es cartera: él se lo paga a
// la DIAN a nombre nuestro y a la empresa le queda el certificado (cuentas
// 1365/1366/1368, que el asiento de venta ya descontó de la CxC). Sin restarlo
// aquí, una factura con retenciones jamás llegaba a saldo cero: el cliente
// pagaba el neto completo y la cartera seguía "debiendo" la retención.
export function getRetencionesFactura(f) {
  if (!f || f.estado === "anulada") return 0;
  // Solo si la venta se contabilizó: la retención nace con el asiento.
  if (!f._asientoVenta) return 0;
  return calcularRetencionesVenta(f).total;
}

export function getSaldoFactura(f) {
  if (f.estado === "pagada") return 0;
  const nc = getTotalNC(f.id);
  const abonado = getTotalAbonadoFactura(f);
  const retenido = getRetencionesFactura(f);
  return Math.max(0, (Number(f.total)||0) - retenido - abonado - nc);
}

export function getTotalCxC() {
  return (state.data.facturas||[])
    .filter(f => f.estado !== "pagada" && f.estado !== "anulada")
    .reduce((s,f) => s + getSaldoFactura(f), 0);
}

export function renderFacturas() {
  const facturas = state.data.facturas || [];
  const filtroEstado = state.factFiltroEstado || "";
  const filtroCliente = state.factFiltroCliente || "";
  const filtroDesde = state.factFiltroDesde || "";
  const filtroHasta = state.factFiltroHasta || "";
  const busqueda = state.factBusqueda || "";

  // Calcular KPIs
  const totalEmitido = facturas.reduce((s,f) => s+(Number(f.total)||0), 0);
  const totalPorCobrar = facturas.filter(f=>f.estado==="emitida"||f.estado==="vencida").reduce((s,f)=>s+getSaldoFactura(f),0);
  const totalCobrado = facturas.filter(f=>f.estado==="pagada").reduce((s,f)=>s+(Number(f.total)||0),0);
  const totalNC = (state.data.notas_credito||[]).filter(nc=>nc.estado!=="anulada").reduce((s,nc)=>s+(Number(nc.total)||0),0);
  const countVencidas = facturas.filter(f=>f.estado==="vencida").length;

  // Filtrar
  let docs = [...facturas];
  if (filtroEstado) docs = docs.filter(f => f.estado === filtroEstado);
  if (filtroCliente) docs = docs.filter(f => f.cliente_id === filtroCliente);
  if (filtroDesde) docs = docs.filter(f => f.fecha >= filtroDesde);
  if (filtroHasta) docs = docs.filter(f => f.fecha <= filtroHasta);
  if (busqueda) {
    const q = busqueda.toLowerCase();
    docs = docs.filter(f => {
      const cli = state.data.clientes.find(c=>c.id===f.cliente_id)||{};
      return (f.numero||"").toLowerCase().includes(q) || (cli.nombre||"").toLowerCase().includes(q);
    });
  }
  docs = docs.sort((a,b) => new Date(b.fecha) - new Date(a.fecha));

  const clientes = state.data.clientes || [];

  const kpiColor = "#059669";
  const kpis = `
    <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(180px,1fr));gap:9px;margin-bottom:10px;">
      <div style="background:white;border:2px solid #059669;border-radius:6px;padding:11px;">
        <div style="font-size:10.5px;color:${C.muted};font-weight:600;margin-bottom:4px;">TOTAL FACTURADO</div>
        <div style="font-size:16px;font-weight:800;color:#059669;">${fmtCOP(totalEmitido)}</div>
        <div style="font-size:10.5px;color:${C.muted};margin-top:4px;">${facturas.length} factura${facturas.length!==1?"s":""}</div>
      </div>
      <div style="background:white;border:2px solid #f59e0b;border-radius:6px;padding:11px;">
        <div style="font-size:10.5px;color:${C.muted};font-weight:600;margin-bottom:4px;">POR COBRAR</div>
        <div style="font-size:16px;font-weight:800;color:#d97706;">${fmtCOP(totalPorCobrar)}</div>
        <div style="font-size:10.5px;color:${C.muted};margin-top:4px;">${facturas.filter(f=>f.estado==="emitida"||f.estado==="vencida").length} pendientes</div>
      </div>
      <div style="background:white;border:2px solid #047857;border-radius:6px;padding:11px;">
        <div style="font-size:10.5px;color:${C.muted};font-weight:600;margin-bottom:4px;">COBRADO</div>
        <div style="font-size:16px;font-weight:800;color:#047857;">${fmtCOP(totalCobrado)}</div>
        <div style="font-size:10.5px;color:${C.muted};margin-top:4px;">${facturas.filter(f=>f.estado==="pagada").length} pagadas</div>
      </div>
      ${countVencidas > 0 ? `
      <div style="background:#fef2f2;border:2px solid #ef4444;border-radius:6px;padding:11px;">
        <div style="font-size:10.5px;color:#b91c1c;font-weight:700;margin-bottom:4px;">⚠️ VENCIDAS</div>
        <div style="font-size:16px;font-weight:800;color:#ef4444;">${countVencidas}</div>
        <div style="font-size:10.5px;color:#b91c1c;margin-top:4px;">requieren atención</div>
      </div>` : ""}
      ${totalNC > 0 ? `
      <div style="background:white;border:2px solid #dc2626;border-radius:6px;padding:11px;">
        <div style="font-size:10.5px;color:${C.muted};font-weight:600;margin-bottom:4px;">🧾 NOTAS CRÉDITO</div>
        <div style="font-size:16px;font-weight:800;color:#dc2626;">${fmtCOP(totalNC)}</div>
        <div style="font-size:10.5px;color:${C.muted};margin-top:4px;">${(state.data.notas_credito||[]).filter(nc=>nc.estado!=="anulada").length} emitidas</div>
      </div>` : ""}
    </div>
  `;

  // Tabla de facturas
  const rows = docs.length === 0
    ? `<tr><td colspan="7" style="text-align:center;padding:32px;color:${C.muted};font-size:12px;">
        ${facturas.length === 0
          ? "No hay facturas aún. Genera una desde una Remisión → 💰 Facturar"
          : "No hay resultados con los filtros aplicados."}
       </td></tr>`
    : docs.map(f => {
        const cli = state.data.clientes.find(c=>c.id===f.cliente_id)||{};
        const estadoLabel = (ESTADOS.FACT||{})[f.estado] || f.estado;
        const estadoColor = ESTADO_COLOR[f.estado] || "#6b7280";
        const hoy = new Date(); hoy.setHours(0,0,0,0);
        const vencimiento = f.fecha_vencimiento ? new Date(f.fecha_vencimiento) : null;
        const estaVencida = vencimiento && vencimiento < hoy && f.estado === "emitida";
        if (estaVencida && f.estado !== "vencida") {
          // Auto-marcar como vencida
          f.estado = "vencida";
          saveData("facturas", state.data.facturas.map(x=>x.id===f.id?f:x));
        }
        const ncTotal = getTotalNC(f.id);
        const saldo = getSaldoFactura(f);
        return `
          <tr style="border-bottom:1px solid ${C.border};">
            <td style="padding:6px 10px;font-weight:700;color:#059669;font-size:12px;">
              ${escapeHTML(f.numero)}
              ${f._local ? `<span style="display:block;font-size:9px;font-weight:600;color:#6b7280;background:#f1f5f9;border-radius:4px;padding:1px 5px;margin-top:2px;width:fit-content;">LOCAL</span>` : ""}
            </td>
            <td style="padding:6px 10px;font-size:11.5px;color:${C.muted};">${fmtDate(f.fecha)}</td>
            <td style="padding:6px 10px;font-size:12px;font-weight:600;">${escapeHTML(cli.nombre||"—")}</td>
            <td style="padding:6px 10px;font-size:11.5px;color:${C.muted};">${f.fecha_vencimiento ? fmtDate(f.fecha_vencimiento) : "—"}</td>
            <td style="padding:6px 10px;text-align:right;">
              <div style="font-weight:700;color:#059669;">${fmtCOP(f.total)}</div>
              ${ncTotal > 0 ? `<div style="font-size:10px;color:#dc2626;">NC: -${fmtCOP(ncTotal)}</div>` : ""}
              ${getTotalAbonadoFactura(f) > 0 ? `<div style="font-size:10px;color:#059669;">Abonado: ${fmtCOP(getTotalAbonadoFactura(f))}</div>` : ""}
              ${(ncTotal > 0 || getTotalAbonadoFactura(f) > 0) && f.estado !== "pagada" ? `<div style="font-size:10px;font-weight:700;color:#d97706;">Saldo: ${fmtCOP(saldo)}</div>` : ""}
            </td>
            <td style="padding:6px 10px;text-align:center;">
              <span style="background:${estadoColor}18;color:${estadoColor};border:1px solid ${estadoColor}40;padding:3px 9px;border-radius:20px;font-size:10.5px;font-weight:700;white-space:nowrap;">${estadoLabel}</span>
            </td>
            <td style="padding:6px 10px;text-align:right;">
              <div style="display:flex;gap:6px;justify-content:flex-end;flex-wrap:wrap;">
                <button data-action="ver-factura" data-id="${f.id}" style="${btnStyle("#059669",null,true)}" title="Ver detalle">👁 Ver</button>
                <button data-action="print-factura" data-id="${f.id}" style="${btnStyle(C.mathiz,null,true)}" title="Ver la factura y descargar el PDF">📄</button>
                ${f.estado === "emitida" || f.estado === "vencida" ? `<button data-action="abonar-factura" data-id="${f.id}" style="${btnStyle("#059669",null,true)}" title="Registrar abono o cobro total">💳 Abonar</button>` : ""}
                ${puedeReenviarFactura(f) ? `<button data-action="reenviar-factura" data-id="${f.id}" style="${btnStyle("#2563eb",null,true)}" title="Reenviar el correo al cliente (no se retransmite a la DIAN)">📧</button>` : ""}
                ${f.estado === "emitida" || f.estado === "vencida" ? `<button data-action="marcar-pagada-factura" data-id="${f.id}" style="${btnStyle("#047857",null,true)}" title="Marcar como cobrada total">💵 Cobrada</button>` : ""}
                ${f.estado !== "anulada" ? `<button data-action="crear-nota-credito" data-id="${f.id}" style="${btnStyle("#dc2626",null,true)}" title="Generar nota crédito">🧾 N.Crédito</button>` : ""}
                ${f.estado !== "anulada" && f.estado !== "pagada" ? `<button data-action="anular-factura" data-id="${f.id}" style="${btnStyle(C.red,null,true)}" title="Anular">❌</button>` : ""}
              </div>
            </td>
          </tr>
        `;
      }).join("");

  return `
    <div>
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:10px;flex-wrap:wrap;gap:9px;">
        <div>
          <h2 style="margin:0;font-size:17px;font-weight:800;color:#059669;">💰 Facturas Electrónicas</h2>
          <div style="font-size:11.5px;color:${C.muted};margin-top:2px;">Gestión de cuentas por cobrar · MTHC-012</div>
        </div>
      </div>

      ${kpis}

      <!-- Filtros -->
      <div style="background:white;border:1px solid ${C.border};border-radius:6px;padding:10px;margin-bottom:10px;display:flex;gap:10px;flex-wrap:wrap;align-items:center;">
        <input id="fact-busqueda" placeholder="🔍 Buscar por número o cliente..." value="${escapeHTML(busqueda)}"
          style="${inputStyle}width:220px;"
          oninput="state.factBusqueda=this.value;render();">
        <select id="fact-filtro-estado" style="${inputStyle}width:180px;cursor:pointer;" onchange="state.factFiltroEstado=this.value;render();">
          <option value="">Todos los estados</option>
          ${Object.entries(ESTADOS.FACT||{}).map(([k,v])=>`<option value="${k}"${filtroEstado===k?" selected":""}>${v}</option>`).join("")}
        </select>
        <select id="fact-filtro-cliente" style="${inputStyle}width:200px;cursor:pointer;" onchange="state.factFiltroCliente=this.value;render();">
          <option value="">Todos los clientes</option>
          ${clientes.map(c=>`<option value="${c.id}"${filtroCliente===c.id?" selected":""}>${escapeHTML(c.nombre)}</option>`).join("")}
        </select>
        <div>
          <label style="font-size:9px;color:${C.muted};display:block;">Desde</label>
          <input type="date" value="${filtroDesde}" style="${inputStyle}width:130px;" onchange="state.factFiltroDesde=this.value;render();"/>
        </div>
        <div>
          <label style="font-size:9px;color:${C.muted};display:block;">Hasta</label>
          <input type="date" value="${filtroHasta}" style="${inputStyle}width:130px;" onchange="state.factFiltroHasta=this.value;render();"/>
        </div>
        ${(filtroEstado||filtroCliente||filtroDesde||filtroHasta||busqueda) ? `<button data-action="limpiar-filtros-fact" style="${btnStyle(null,"secondary")}">✕ Limpiar</button>` : ""}
        <div style="margin-left:auto;font-size:11.5px;color:${C.muted};">${docs.length} de ${facturas.length}</div>
      </div>

      <!-- Tabla -->
      <div style="background:white;border:1px solid ${C.border};border-radius:6px;overflow:hidden;">
        <table style="width:100%;border-collapse:collapse;">
          <thead>
            <tr style="background:#f8fafc;border-bottom:2px solid #05966920;">
              <th style="padding:6px 10px;text-align:left;font-size:10.5px;color:${C.muted};font-weight:700;">N° FACTURA</th>
              <th style="padding:6px 10px;text-align:left;font-size:10.5px;color:${C.muted};font-weight:700;">FECHA</th>
              <th style="padding:6px 10px;text-align:left;font-size:10.5px;color:${C.muted};font-weight:700;">CLIENTE</th>
              <th style="padding:6px 10px;text-align:left;font-size:10.5px;color:${C.muted};font-weight:700;">VENCE</th>
              <th style="padding:6px 10px;text-align:right;font-size:10.5px;color:${C.muted};font-weight:700;">TOTAL</th>
              <th style="padding:6px 10px;text-align:center;font-size:10.5px;color:${C.muted};font-weight:700;">ESTADO</th>
              <th style="padding:6px 10px;text-align:right;font-size:10.5px;color:${C.muted};font-weight:700;">ACCIONES</th>
            </tr>
          </thead>
          <tbody>${rows}</tbody>
        </table>
      </div>

      <!-- Nota -->
      <div style="margin-top:14px;padding:9px;background:#f8fafc;border:1px solid #bbf7d0;border-radius:6px;font-size:11.5px;color:#065f46;">
        💡 Las facturas se generan automáticamente desde <strong>Remisiones → 💰 Facturar</strong>. Se integran con Dataico para facturación electrónica DIAN.
      </div>
    </div>
  `;
}

export function showVerFactura(factId) {
  const f = (state.data.facturas||[]).find(x=>x.id===factId);
  if (!f) return;
  const cli = state.data.clientes.find(c=>c.id===f.cliente_id)||{};
  const estadoLabel = (ESTADOS.FACT||{})[f.estado] || f.estado;
  const estadoColor = ESTADO_COLOR[f.estado] || "#6b7280";

  const itemsHTML = (f.items||[]).map((it,i) => `
    <tr style="background:${i%2===0?"#f9fafb":"white"};">
      <td style="padding:6px 10px;">${i+1}</td>
      <td style="padding:6px 10px;font-weight:600;">${escapeHTML(it.articulo)}</td>
      <td style="padding:6px 10px;">${escapeHTML(it.descripcion||"")}</td>
      <td style="padding:6px 10px;">${escapeHTML(it.referencia||"")}</td>
      <td style="padding:6px 10px;text-align:right;">${it.cantidad}</td>
      <td style="padding:6px 10px;text-align:right;">${fmtCOP(it.precio_unitario)}</td>
      <td style="padding:6px 10px;text-align:right;font-weight:700;">${fmtCOP(it.total)}</td>
    </tr>
  `).join("");

  state.modal = `
    <div data-action="close-modal-bg" style="position:fixed;top:0;right:0;bottom:0;left:0;background:rgba(15,23,42,.5);display:flex;align-items:center;justify-content:center;z-index:1000;padding:11px;">
      <div data-action="prevent-close" style="background:white;border-radius:14px;width:700px;max-width:100%;max-height:90vh;overflow:auto;box-shadow:0 20px 60px rgba(0,0,0,.25);">
        <div style="padding:10px 12px;border-bottom:1px solid ${C.border};background:#f8fafc;display:flex;justify-content:space-between;align-items:center;">
          <div>
            <h3 style="margin:0;font-size:16px;font-weight:800;color:#059669;">💰 ${escapeHTML(f.numero)}</h3>
            <div style="font-size:11.5px;color:${C.muted};margin-top:2px;">${fmtDate(f.fecha)} ${f.fecha_vencimiento ? "· Vence: "+fmtDate(f.fecha_vencimiento) : ""}</div>
          </div>
          <div style="display:flex;gap:8px;align-items:center;">
            <span style="background:${estadoColor}18;color:${estadoColor};border:1px solid ${estadoColor}40;padding:4px 12px;border-radius:20px;font-size:11.5px;font-weight:700;">${estadoLabel}</span>
            ${botonBitacora("facturas", f.id, f.numero, { referencia: f.numero })}
            <button data-action="close-modal" style="background:transparent;border:none;font-size:16px;color:${C.muted};cursor:pointer;">✕</button>
          </div>
        </div>
        <div style="padding:11px;">
          <!-- Cliente -->
          <div style="background:#f8fafc;border-radius:6px;padding:10px;margin-bottom:10px;display:flex;justify-content:space-between;flex-wrap:wrap;gap:10px;">
            <div>
              <div style="font-size:10.5px;color:${C.muted};font-weight:700;margin-bottom:4px;">CLIENTE</div>
              <div style="font-size:12px;font-weight:700;">${escapeHTML(cli.nombre||"—")}</div>
              <div style="font-size:11.5px;color:${C.muted};">NIT ${escapeHTML(cli.nit||"—")} · ${escapeHTML(cli.telefono||"")}</div>
              ${cli.email?`<div style="font-size:10.5px;color:${C.muted};">${escapeHTML(cli.email)}</div>`:""}
            </div>
            ${f._origen ? `
              <div style="text-align:right;">
                <div style="font-size:10.5px;color:${C.muted};font-weight:700;margin-bottom:4px;">ORIGEN</div>
                <div style="font-size:12px;font-weight:700;color:${C.cyan};">📮 ${escapeHTML(f._origen.numero)}</div>
                <div style="font-size:10.5px;color:${C.muted};">${fmtDate(f._origen.fecha)}</div>
              </div>
            ` : ""}
          </div>

          <!-- Dataico info -->
          ${f._dataico && f._dataico.real ? `
            <div style="background:#ecfdf5;border:1px solid #86efac;border-radius:6px;padding:9px;margin-bottom:10px;font-size:11.5px;">
              <div style="font-weight:700;color:#065f46;margin-bottom:4px;">🇨🇴 Factura electrónica emitida ante la DIAN vía Dataico</div>
              <div style="color:${C.muted};word-break:break-all;font-size:10px;">CUFE: <span style="color:${C.text};">${f._dataico.cufe||"— (consultar en el panel de Dataico)"}</span></div>
            </div>
          ` : f._dataico ? `
            <div style="background:#fef3c7;border:1px solid #fcd34d;border-radius:6px;padding:9px;margin-bottom:10px;font-size:11.5px;">
              <div style="font-weight:700;color:#92400e;margin-bottom:4px;">⚠️ CUFE simulado — emitida antes de conectar Dataico</div>
              <div style="color:#78350f;">Este CUFE se generó localmente y NO tiene validez fiscal ante la DIAN.</div>
              <div style="color:#78350f;margin-top:4px;">Ambiente registrado: ${f._dataico.ambiente==="prod"?"🟢 Producción":"🟡 Pruebas"}</div>
              <div style="color:${C.muted};margin-top:2px;word-break:break-all;font-size:10px;">CUFE (simulado): ${f._dataico.cufe||"—"}</div>
            </div>
          ` : f._local ? `
            <div style="background:#f8fafc;border:1px solid #e2e8f0;border-radius:6px;padding:9px;margin-bottom:10px;font-size:11.5px;">
              <div style="font-weight:700;color:#6b7280;margin-bottom:4px;">📄 Factura Local (sin validez fiscal DIAN)</div>
              <div style="color:#9ca3af;">Pendiente emisión electrónica con Dataico. Una vez configurado, re-emite esta factura para obtener validez fiscal.</div>
            </div>
          ` : ""}

          <!-- Items -->
          <table style="width:100%;border-collapse:collapse;margin-bottom:9px;font-size:11.5px;">
            <thead>
              <tr style="background:#059669;color:white;">
                <th style="padding:8px 10px;text-align:left;">#</th>
                <th style="padding:8px 10px;text-align:left;">Artículo</th>
                <th style="padding:8px 10px;text-align:left;">Descripción</th>
                <th style="padding:8px 10px;text-align:left;">Referencia</th>
                <th style="padding:8px 10px;text-align:right;">Cant</th>
                <th style="padding:8px 10px;text-align:right;">P.Unit</th>
                <th style="padding:8px 10px;text-align:right;">Total</th>
              </tr>
            </thead>
            <tbody>${itemsHTML}</tbody>
          </table>

          <!-- Totales -->
          <div style="text-align:right;padding:12px 0;border-top:2px solid #059669;">
            <div style="font-size:11.5px;color:${C.muted};">Subtotal: <strong>${fmtCOP(f.subtotal)}</strong></div>
            ${renglonesIVA(f.items, f.iva).map(r => `<div style="font-size:11.5px;color:${C.muted};">${escapeHTML(r.lbl)}: <strong>${fmtCOP(r.valor)}</strong></div>`).join("")}
            <div style="font-size:17px;font-weight:800;color:#059669;">TOTAL: ${fmtCOP(f.total)}</div>
            ${(() => {
              const _ret = getRetencionesFactura(f);
              if (_ret <= 0) return "";
              // El cliente es agente retenedor: paga el neto y el resto llega
              // como certificado (queda en 1365/1366/1368 a favor).
              return `<div style="font-size:11px;color:#92400e;margin-top:4px;">(−) Retenciones del cliente: <strong>${fmtCOP(_ret)}</strong></div>
                <div style="font-size:12.5px;font-weight:800;color:#0E7490;">NETO A COBRAR: ${fmtCOP((Number(f.total)||0) - _ret)}</div>`;
            })()}
          </div>

          ${f.notas ? `<div style="margin-top:10px;padding:10px;background:#fffbeb;border-left:3px solid ${C.amber};border-radius:4px;font-size:11.5px;"><strong>Notas:</strong> <span style="white-space:pre-line;">${htmlParaMostrar(f.notas)}</span></div>` : ""}

          <div style="display:flex;gap:10px;justify-content:flex-end;padding-top:16px;border-top:1px solid ${C.border};margin-top:16px;flex-wrap:wrap;">
            <button data-action="close-modal" style="${btnStyle(null,"secondary")}">Cerrar</button>
            <button data-action="print-factura" data-id="${f.id}" style="${btnStyle(C.mathiz)}">📄 Ver / Descargar PDF</button>
            ${puedeReenviarFactura(f) ? `<button data-action="reenviar-factura" data-id="${f.id}" style="${btnStyle("#2563eb")}" title="Vuelve a enviarle el correo al cliente. No se retransmite a la DIAN.">📧 Reenviar al cliente</button>` : ""}
            ${f.estado==="emitida"||f.estado==="vencida" ? `<button data-action="abonar-factura" data-id="${f.id}" style="${btnStyle("#059669")}">💳 Registrar abono</button>` : ""}
            ${f.estado==="emitida"||f.estado==="vencida" ? `<button data-action="marcar-pagada-factura" data-id="${f.id}" style="${btnStyle("#047857")}">💵 Cobrada total</button>` : ""}
          </div>
        </div>
      </div>
    </div>
  `;
  render();
}

export function showAbonarFactura(factId) {
  const f = (state.data.facturas||[]).find(x=>x.id===factId);
  if (!f) return;
  const saldo = getSaldoFactura(f);
  const abonados = f._pagos || [];
  const totalAbonado = getTotalAbonadoFactura(f);

  // La orden de esta factura se sigue por la cadena real de origen (FACT ← RC ←
  // OP). (Antes se buscaba por f._rcId/f._opId, campos que nunca se escribían:
  // los anticipos JAMÁS aparecían en esta ventana.) Pero los anticipos que se
  // ofrecen son TODOS los vivos del cliente, no solo los de esta orden: un
  // saldo a favor que quedó de una compra anterior sirve para esta.
  const rcOrig = (f._origen && f._origen.tipo === "RC")
    ? (state.data.remisiones||[]).find(r => r.id === f._origen.id)
    : null;
  const opId = (rcOrig && rcOrig._origen && rcOrig._origen.tipo === "OP" && rcOrig._origen.id)
    || (f._origen && f._origen.tipo === "OP" && f._origen.id)
    || null;
  const anticiposDisp = anticiposVivosCliente(f.cliente_id, opId);

  const seccionAnticipos = (anticiposDisp.length > 0) ? `
    <div style="margin-bottom:10px;background:#f8fafc;border:1px solid #bbf7d0;border-radius:6px;padding:12px 14px;">
      <div style="font-size:10.5px;font-weight:700;color:#065f46;margin-bottom:8px;text-transform:uppercase;letter-spacing:.4px;">💵 Anticipos disponibles del cliente</div>
      ${anticiposDisp.map(({ op, ant, disponible, propia }) => {
        const usado = anticipoAplicado(ant);
        // Nunca más de lo que falta por cobrar: aplicar de más dejaría la
        // cartera del cliente en negativo. Lo que sobre queda a su favor.
        const tope = Math.min(disponible, saldo);
        return `
      <div style="padding:7px 0;border-bottom:1px solid #dcfce7;">
        <div style="display:flex;justify-content:space-between;align-items:flex-start;gap:10px;">
          <div style="min-width:0;">
            <div style="font-weight:700;font-size:12px;color:#059669;">${fmtCOP(disponible)} disponible</div>
            <div style="font-size:10px;color:#6b7280;">${fmtDate(ant.fecha)} · ${ant.medio||"—"}${ant.referencia?" · "+escapeHTML(ant.referencia):""}</div>
            <div style="font-size:10px;color:${propia ? "#065f46" : "#b45309"};font-weight:600;margin-top:1px;">
              ${escapeHTML(op.numero||"OP")}${propia ? " · de esta orden" : " · saldo a favor de otra orden"}
            </div>
            ${usado > 0 ? `<div style="font-size:10px;color:#94a3b8;">De ${fmtCOP(parseNumber(ant.monto))} recibidos, ${fmtCOP(usado)} ya aplicados.</div>` : ""}
          </div>
          <div style="display:flex;gap:6px;align-items:center;flex-shrink:0;">
            <input data-ant-monto="${ant.id}" type="number" inputmode="decimal" min="0" max="${tope.toFixed(0)}"
              value="${tope.toFixed(0)}" style="${inputStyle}width:118px;text-align:right;font-weight:700;padding:5px 7px;font-size:11.5px;"/>
            <button data-action="cruzar-anticipo-factura" data-fact-id="${factId}" data-ant-id="${ant.id}" data-op-id="${op.id}"
              style="background:#059669;color:white;border:none;padding:6px 12px;border-radius:7px;font-size:10.5px;font-weight:700;cursor:pointer;white-space:nowrap;">
              Cruzar →
            </button>
          </div>
        </div>
        ${disponible > saldo ? `<div style="font-size:10px;color:#b45309;margin-top:4px;">El anticipo alcanza para más de lo que falta por cobrar: quedarán ${fmtCOP(disponible - saldo)} a favor del cliente, para devolver o aplicar a su próxima compra.</div>` : ""}
      </div>`;
      }).join("")}
      <div style="font-size:10.5px;color:#065f46;margin-top:8px;">Puedes aplicar todo el anticipo o solo una parte. Lo que no apliques sigue disponible en su orden.</div>
    </div>
  ` : "";

  const historial = abonados.length > 0 ? `
    <div style="margin-bottom:10px;">
      <div style="font-size:10.5px;font-weight:700;color:${C.muted};margin-bottom:6px;">HISTORIAL DE ABONOS</div>
      <div style="background:#f8fafc;border:1px solid ${C.border};border-radius:6px;overflow:hidden;">
        ${abonados.map(p => `
          <div style="display:flex;justify-content:space-between;align-items:center;padding:6px 10px;border-bottom:1px solid ${C.border};">
            <div>
              <div style="font-size:11.5px;font-weight:600;">${fmtCOP(parseNumber(p.monto))}</div>
              <div style="font-size:10.5px;color:${C.muted};">${fmtDate(p.fecha)} · ${(MEDIOS_PAGO.find(m=>m.value===p.medio)||{label:p.medio||"—"}).label}</div>
              ${p.referencia ? `<div style="font-size:10.5px;color:${C.muted};">Ref: ${escapeHTML(p.referencia)}</div>` : ""}
            </div>
          </div>
        `).join("")}
        <div style="padding:6px 10px;font-size:11.5px;font-weight:700;color:#059669;">Total abonado: ${fmtCOP(totalAbonado)}</div>
      </div>
    </div>
  ` : "";

  state.modal = `
    <div data-action="close-modal-bg" style="position:fixed;top:0;right:0;bottom:0;left:0;background:rgba(15,23,42,.55);display:flex;align-items:center;justify-content:center;z-index:1100;padding:11px;">
      <div style="background:white;border-radius:14px;width:460px;max-width:100%;box-shadow:0 20px 60px rgba(0,0,0,.3);max-height:90vh;overflow-y:auto;">
        <div style="padding:18px 20px;border-bottom:1px solid ${C.border};background:#ecfdf5;border-radius:14px 14px 0 0;">
          <h3 style="margin:0;font-size:16px;color:#065f46;font-weight:700;">💳 Registrar abono — ${escapeHTML(f.numero)}</h3>
          <div style="font-size:11.5px;color:#065f46;margin-top:4px;">Saldo pendiente: <strong>${fmtCOP(saldo)}</strong></div>
        </div>
        <div style="padding:11px;">
          ${seccionAnticipos}
          ${historial}
          <div style="display:grid;grid-template-columns:1fr 1fr;gap:9px;margin-bottom:12px;">
            <div>
              <label style="font-size:10.5px;color:${C.muted};font-weight:600;">Fecha del abono</label>
              <input id="abono-fact-fecha" type="date" value="${today()}" style="${inputStyle}margin-top:4px;"/>
            </div>
            <div>
              <label style="font-size:10.5px;color:${C.muted};font-weight:600;">Monto</label>
              <div style="display:flex;gap:6px;margin-top:4px;">
                <input id="abono-fact-monto" type="number" inputmode="decimal" placeholder="0" value="${saldo.toFixed(0)}" style="${inputStyle}flex:1;text-align:right;"/>
                <button data-action="abono-fact-saldo-total" style="${btnStyle("#059669",null,true)};white-space:nowrap;">Total</button>
              </div>
            </div>
          </div>
          <div style="margin-bottom:12px;">
            <label style="font-size:10.5px;color:${C.muted};font-weight:600;">Medio de pago</label>
            <select id="abono-fact-medio" style="${inputStyle}margin-top:4px;">
              ${MEDIOS_PAGO.map(m=>`<option value="${m.value}">${m.label}</option>`).join("")}
            </select>
          </div>
          <div style="margin-bottom:10px;">
            <label style="font-size:10.5px;color:${C.muted};font-weight:600;">Referencia / notas (opcional)</label>
            <input id="abono-fact-ref" type="text" placeholder="N° transferencia, cheque, etc." style="${inputStyle}margin-top:4px;"/>
          </div>
          <div style="display:flex;gap:10px;justify-content:flex-end;">
            <button data-action="close-modal" style="${btnStyle(null,"secondary")}">Cancelar</button>
            <button data-action="confirmar-abono-factura" data-id="${f.id}" style="${btnStyle("#059669")}">✅ Registrar abono</button>
          </div>
        </div>
      </div>
    </div>
  `;
  state._abonoFactId = f.id;
  render();
}

export function showCrearNotaCredito(facturaId) {
  const f = (state.data.facturas||[]).find(x=>x.id===facturaId);
  if (!f) return;
  state.editingNC = {
    facturaId,
    motivo: "",
    items: (f.items||[]).map(it => ({
      articulo: it.articulo || "",
      descripcion: it.descripcion || "",
      referencia: it.referencia || "",
      cantidad_factura: parseNumber(it.cantidad),
      precio_unitario: parseNumber(it.precio_unitario),
      // La nota crédito reversa lo FACTURADO, así que hereda el régimen de IVA
      // con el que salió cada línea. Si el producto se reclasificó después,
      // reversar con la clasificación nueva devolvería un IVA que nunca se cobró.
      iva_tipo: it.iva_tipo || null,
      iva_pct: it.iva_pct ?? null,
      cantidad: 0
    }))
  };
  renderNotaCreditoModal();
}

import { registerActions } from "../ui/actions.js";

registerActions({
  "facturar-op": (d, el, e) => {showFacturarOP(d.id);
      return;},
  "facturar-rc": (d, el, e) => {showFacturarDataico(d.id);
      return;},
  "ver-factura": (d, el, e) => {showVerFactura(d.id);
      return;},
  "abonar-factura": (d, el, e) => {showAbonarFactura(d.id);
      return;},
  "abono-fact-saldo-total": (d, el, e) => {{
      const fAbono = (state.data.facturas||[]).find(x=>x.id===state._abonoFactId);
      if (fAbono) {
        const inp = document.getElementById("abono-fact-monto");
        if (inp) inp.value = getSaldoFactura(fAbono).toFixed(0);
      }
      return;
    }},
  "confirmar-abono-factura": async (d, el, e) => {{
      const fAb = (state.data.facturas||[]).find(x=>x.id===d.id);
      if (!fAb) return;
      const montoAb = parseNumber(document.getElementById("abono-fact-monto")?.value || "0");
      if (montoAb <= 0) { alert("El monto del abono debe ser mayor a cero"); return; }
      const saldoAb = getSaldoFactura(fAb);
      if (montoAb > saldoAb + 0.01) {
        if (!confirm(`El monto (${fmtCOP(montoAb)}) supera el saldo pendiente (${fmtCOP(saldoAb)}). ¿Continuar?`)) return;
      }
      const fechaAb = document.getElementById("abono-fact-fecha")?.value || today();
      const medioAb = document.getElementById("abono-fact-medio")?.value || "transferencia";
      const refAb = document.getElementById("abono-fact-ref")?.value || "";
      const nuevoPago = { monto: montoAb, fecha: fechaAb, medio: medioAb, referencia: refAb, registrado_en: new Date().toISOString() };
      const pagosAb = [...(fAb._pagos || []), nuevoPago];
      // "Pagada" cuando el SALDO REAL llega a cero: total − retenciones del
      // cliente − notas crédito − abonos. Compararlo contra el total a secas
      // dejaba las facturas con retención eternamente "emitidas" aunque el
      // cliente ya hubiera pagado todo su neto.
      const saldoRestanteAb = getSaldoFactura({ ...fAb, _pagos: pagosAb });
      const nuevoEstadoAb = saldoRestanteAb <= 0.01 ? "pagada" : fAb.estado;
      const updFAb = { ...fAb, _pagos: pagosAb, estado: nuevoEstadoAb, fecha_cobro: nuevoEstadoAb === "pagada" ? today() : fAb.fecha_cobro };
      // Generar asiento contable de cobro por CADA abono (cada uno es una entrada real de caja/banco);
      // antes los abonos intermedios quedaban sin asiento y el banco/CxC se subestimaba.
      try {
        const asientoAb = await generarAsientoCobroFactura(updFAb, montoAb, medioAb, fechaAb);
        updFAb._asientoCobro = asientoAb.id;
      } catch (err) {
        alert("⚠️ No se pudo registrar el abono: " + err.message);
        return;
      }
      saveData("facturas", state.data.facturas.map(x=>x.id===fAb.id?updFAb:x));
      state._abonoFactId = null;
      state.modal = null;
      render();
      return;
    }},
  "ir-a-factura": (d, el, e) => {state.modal = null;
      state.page = "FACT";
      render();
      return;},
  "print-factura": (d, el, e) => {state.modal = null;
      exportPDFFactura(d.id);
      return;},
  "marcar-pagada-factura": async (d, el, e) => {{
      state.modal = null;
      const fObj = (state.data.facturas||[]).find(x=>x.id===d.id);
      if (!fObj) return;
      if (!confirm(`¿Marcar ${fObj.numero} como COBRADA?`)) return;
      const saldoPendienteF = getSaldoFactura(fObj); // calcular ANTES de marcarla pagada (pagada → saldo 0)
      const updF = { ...fObj, estado:"pagada", fecha_cobro: today() };
      // Asiento de cobro solo por el saldo que faltaba: los abonos previos ya tienen su propio asiento
      if (saldoPendienteF > 0) {
        try {
          const asientoCobro = await generarAsientoCobroFactura(updF, saldoPendienteF, "transferencia");
          updF._asientoCobro = asientoCobro.id;
        } catch (err) {
          alert("⚠️ No se pudo registrar el cobro: " + err.message);
          return;
        }
      }
      saveData("facturas", state.data.facturas.map(x=>x.id===d.id?updF:x));
      render();
      return;
    }},
  "anular-factura": async (d, el, e) => {{
      state.modal = null;
      const fObj2 = (state.data.facturas||[]).find(x=>x.id===d.id);
      if (!fObj2) return;
      if (!confirm(`¿Anular ${fObj2.numero}? Esta acción no se puede deshacer.`)) return;
      const updF2 = { ...fObj2, estado:"anulada", fecha_anulacion: today() };
      // Reversa contable del asiento de venta (deja el registro trazado en el Libro Diario)
      if (fObj2._asientoVenta) {
        const astVenta = (state.data.asientos||[]).find(a => a.id === fObj2._asientoVenta);
        if (astVenta) {
          try {
            const reversa = await crearAsiento(
              `Anulación Factura ${fObj2.numero} (reversa de ${astVenta.numero})`,
              { tipo:"FACT", id: fObj2.id, numero: fObj2.numero },
              (astVenta.movimientos||[]).map(m => ({ cuenta: m.cuenta, debito: m.credito, credito: m.debito, tercero_id: m.tercero_id })),
              today()
            );
            updF2._asientoAnulacion = reversa.id;
            // Reponer el kardex si la venta había descontado costo/inventario
            if ((astVenta.movimientos||[]).some(m => m.cuenta === "6135" && m.debito > 0)) {
              (fObj2.items||[]).forEach(it => {
                const cant = parseNumber(it.cantidad)||0;
                if (cant) ajustarInventario(it.articulo, cant, { tipo:"FACT", id: fObj2.id, numero: fObj2.numero + " (anulación)" });
              });
            }
          } catch (err) {
            alert("⚠️ La factura se anulará, pero no se pudo registrar la reversa contable: " + err.message);
          }
        }
      }
      saveData("facturas", state.data.facturas.map(x=>x.id===d.id?updF2:x));
      // Revertir RC si existe
      if (fObj2._origen && fObj2._origen.tipo==="RC") {
        const rcObj = (state.data.remisiones||[]).find(r=>r.id===fObj2._origen.id);
        if (rcObj && rcObj.estado==="facturada") {
          const updRc = { ...rcObj, estado:"completada" };
          delete updRc._convertedTo;
          delete updRc._factura;
          saveData("remisiones", state.data.remisiones.map(r=>r.id===rcObj.id?updRc:r));
        }
      }
      render();
      return;
    }},
  "limpiar-filtros-fact": (d, el, e) => {state.factFiltroEstado = "";
      state.factFiltroCliente = "";
      state.factFiltroDesde = "";
      state.factFiltroHasta = "";
      state.factBusqueda = "";
      render();
      return;},
  "crear-nota-credito": (d, el, e) => {showCrearNotaCredito(d.id);
      return;},
  "facturar-local": async (d, el, e) => {await facturarLocal();
      return;},
});
